// src/components/serverSetup/CreateRoleModal.js

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Checkbox,
  FormControlLabel,
  Button,
  Typography,
  Box,
  Snackbar,
  Alert,
  Grid,
  Tooltip,
} from '@mui/material';
import axios from 'axios';
import { PERMISSION_BITFIELD } from '../../utils/permissions'; // Ensure correct path

const CreateRoleModal = ({
  open,
  onClose,
  accessToken,
  selectedGuild,
  onRoleCreated, // Callback to refresh roles in parent component
}) => {
  const [roleName, setRoleName] = useState('');
  const [roleColor, setRoleColor] = useState('#728C63'); // Set default brand color
  const [roleHoist, setRoleHoist] = useState(false);
  const [roleMentionable, setRoleMentionable] = useState(false);
  const [rolePermissions, setRolePermissions] = useState([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const permissionCategories = [
    {
      category: 'General Permissions',
      permissions: [
        { name: 'Create Instant Invite', value: 'CREATE_INSTANT_INVITE', description: 'Allows creation of instant invites.' },
        { name: 'Kick Members', value: 'KICK_MEMBERS', description: 'Allows kicking members from the guild.' },
        { name: 'Ban Members', value: 'BAN_MEMBERS', description: 'Allows banning members from the guild.' },
        { name: 'Administrator', value: 'ADMINISTRATOR', description: 'Gives all permissions and bypasses channel permissions.' },
        { name: 'Manage Guild', value: 'MANAGE_GUILD', description: 'Allows management and editing of the guild.' },
        { name: 'View Audit Log', value: 'VIEW_AUDIT_LOG', description: 'Allows viewing the audit log.' },
      ],
    },
    {
      category: 'Text Permissions',
      permissions: [
        { name: 'Manage Channels', value: 'MANAGE_CHANNELS', description: 'Allows management of channels.' },
        { name: 'Add Reactions', value: 'ADD_REACTIONS', description: 'Allows adding reactions to messages.' },
        { name: 'View Channel', value: 'VIEW_CHANNEL', description: 'Allows viewing channels.' },
        { name: 'Send Messages', value: 'SEND_MESSAGES', description: 'Allows sending messages in channels.' },
        { name: 'Send TTS Messages', value: 'SEND_TTS_MESSAGES', description: 'Allows sending text-to-speech messages.' },
        { name: 'Manage Messages', value: 'MANAGE_MESSAGES', description: 'Allows deleting and pinning messages.' },
        { name: 'Embed Links', value: 'EMBED_LINKS', description: 'Allows embedding links in messages.' },
        { name: 'Attach Files', value: 'ATTACH_FILES', description: 'Allows attaching files to messages.' },
        { name: 'Read Message History', value: 'READ_MESSAGE_HISTORY', description: 'Allows reading message history.' },
        { name: 'Mention Everyone', value: 'MENTION_EVERYONE', description: 'Allows mentioning @everyone and @here.' },
        { name: 'Use External Emojis', value: 'USE_EXTERNAL_EMOJIS', description: 'Allows using emojis from other servers.' },
        { name: 'View Guild Insights', value: 'VIEW_GUILD_INSIGHTS', description: 'Allows viewing guild insights.' },
      ],
    },
    {
      category: 'Voice Permissions',
      permissions: [
        { name: 'Connect', value: 'CONNECT', description: 'Allows joining voice channels.' },
        { name: 'Speak', value: 'SPEAK', description: 'Allows speaking in voice channels.' },
        { name: 'Mute Members', value: 'MUTE_MEMBERS', description: 'Allows muting members in voice channels.' },
        { name: 'Deafen Members', value: 'DEAFEN_MEMBERS', description: 'Allows deafening members in voice channels.' },
        { name: 'Move Members', value: 'MOVE_MEMBERS', description: 'Allows moving members between voice channels.' },
        { name: 'Use VAD', value: 'USE_VAD', description: 'Allows using voice activity detection.' },
      ],
    },
    {
      category: 'Advanced Permissions',
      permissions: [
        { name: 'Change Nickname', value: 'CHANGE_NICKNAME', description: 'Allows changing own nickname.' },
        { name: 'Manage Nicknames', value: 'MANAGE_NICKNAMES', description: 'Allows managing other members\' nicknames.' },
        { name: 'Manage Roles', value: 'MANAGE_ROLES', description: 'Allows managing roles.' },
        { name: 'Manage Webhooks', value: 'MANAGE_WEBHOOKS', description: 'Allows managing webhooks.' },
        { name: 'Manage Emojis and Stickers', value: 'MANAGE_EMOJIS_AND_STICKERS', description: 'Allows managing emojis and stickers.' },
      ],
    },
  ];

  const handlePermissionChange = (e) => {
    const { value, checked } = e.target;
    if (checked) {
      setRolePermissions((prev) => [...prev, value]);
    } else {
      setRolePermissions((prev) => prev.filter((perm) => perm !== value));
    }
  };

  const handleCreateRole = async () => {
    if (!roleName.trim()) {
      setSnackbar({
        open: true,
        message: 'Role name is required.',
        severity: 'warning',
      });
      return;
    }

    // Validate the color format (optional but recommended)
    const colorHex = roleColor.replace('#', '');
    if (!/^([0-9A-Fa-f]{6})$/.test(colorHex)) {
      setSnackbar({
        open: true,
        message: 'Invalid color format.',
        severity: 'error',
      });
      return;
    }

    // Convert the hex color to an integer
    const colorInt = parseInt(colorHex, 16);

    // Calculate the total permission bitfield
    const totalPermissions = rolePermissions.reduce((acc, perm) => {
      return acc + (PERMISSION_BITFIELD[perm] || 0);
    }, 0);

    console.log('Role Creation Payload:', {
      name: roleName,
      permissions: totalPermissions,
      color: colorInt,
      hoist: roleHoist,
      mentionable: roleMentionable,
    }); // Debugging

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/discord/guilds/${selectedGuild}/roles`,
        {
          name: roleName,
          permissions: totalPermissions, // Send the bitfield
          color: colorInt, // Send as integer
          hoist: roleHoist,
          mentionable: roleMentionable,
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.data) {
        setSnackbar({
          open: true,
          message: 'Role created successfully!',
          severity: 'success',
        });
        // Reset form fields
        setRoleName('');
        setRoleColor('#728C63'); // Reset to default brand color
        setRoleHoist(false);
        setRoleMentionable(false);
        setRolePermissions([]);
        // Callback to parent to refresh roles
        onRoleCreated();
        // Close the modal after a short delay
        setTimeout(() => {
          onClose();
          setSnackbar({ ...snackbar, open: false });
        }, 1500);
      }
    } catch (error) {
      console.error('Error creating role:', error);
      // Log detailed error response
      console.error('Error response data:', error.response?.data);
      const errorMsg = error.response?.data?.error || error.message;
      setSnackbar({
        open: true,
        message: `Failed to create role: ${errorMsg}`,
        severity: 'error',
      });
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle
        sx={{
          backgroundColor: '#081529',
          color: '#A1D1F0',
          fontFamily: 'Poppins, sans-serif',
        }}
      >
        Create New Role
      </DialogTitle>
      <DialogContent
        sx={{
          backgroundColor: '#0A1B34',
          color: '#E0E4ED',
          fontFamily: 'Poppins, sans-serif',
        }}
      >
        <Box component="form" noValidate autoComplete="off">
          {/* Role Name */}
          <TextField
            label="Role Name"
            value={roleName}
            onChange={(e) => setRoleName(e.target.value)}
            fullWidth
            margin="normal"
            required
            InputProps={{
              style: {
                color: '#E0E4ED',
                backgroundColor: '#132A47',
                borderRadius: '8px',
              },
            }}
            InputLabelProps={{
              style: {
                color: '#D1E5F0',
              },
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: '#607080',
                },
                '&:hover fieldset': {
                  borderColor: '#9AC0CD',
                },
              },
            }}
          />
          {/* Role Color and Options */}
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, flexWrap: 'wrap', gap: 2 }}>
            {/* Role Color Picker Container */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                padding: '8px',
                backgroundColor: '#132A47',
                borderRadius: '8px',
              }}
            >
              <Typography variant="body1" sx={{ color: '#A1D1F0' }}>
                Role Color:
              </Typography>
              <input
                type="color"
                value={roleColor}
                onChange={(e) => setRoleColor(e.target.value)}
                style={{
                  border: 'none',
                  padding: 0,
                  background: 'none',
                  cursor: 'pointer',
                  width: '40px',
                  height: '40px',
                  borderRadius: '4px',
                }}
                aria-label="Role Color Picker"
              />
            </Box>

            {/* Role Options */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={roleHoist}
                  onChange={(e) => setRoleHoist(e.target.checked)}
                  sx={{ color: '#A1D1F0' }}
                />
              }
              label="Display role members separately"
              sx={{ color: '#E0E4ED' }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={roleMentionable}
                  onChange={(e) => setRoleMentionable(e.target.checked)}
                  sx={{ color: '#A1D1F0' }}
                />
              }
              label="Allow anyone to mention this role"
              sx={{ color: '#E0E4ED' }}
            />
          </Box>

          {/* Role Permissions */}
          <Typography variant="subtitle1" sx={{ mt: 3, mb: 1 }}>
            Role Permissions:
          </Typography>
          {permissionCategories.map((category, index) => (
            <Box key={index} sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ color: '#A1D1F0', mb: 1 }}>
                {category.category}
              </Typography>
              <Grid container spacing={2}>
                {category.permissions.map((perm) => (
                  <Grid item xs={12} sm={6} md={4} key={perm.value}>
                    <Tooltip title={perm.description} arrow>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={rolePermissions.includes(perm.value)}
                            onChange={handlePermissionChange}
                            value={perm.value}
                            sx={{ color: '#A1D1F0' }}
                          />
                        }
                        label={perm.name}
                        sx={{ color: '#E0E4ED' }}
                      />
                    </Tooltip>
                  </Grid>
                ))}
              </Grid>
            </Box>
          ))}
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          backgroundColor: '#081529',
          padding: '16px',
        }}
      >
        <Button
          onClick={onClose}
          sx={{
            color: '#A1D1F0',
            borderColor: '#A1D1F0',
            textTransform: 'none',
            fontFamily: 'Poppins, sans-serif',
            '&:hover': {
              borderColor: '#9AC0CD',
              backgroundColor: 'rgba(154, 192, 205, 0.1)',
            },
          }}
          variant="outlined"
        >
          Cancel
        </Button>
        <Button
          onClick={handleCreateRole}
          sx={{
            color: '#FFFFFF',
            backgroundColor: '#728C63', // Brand color
            textTransform: 'none',
            fontFamily: 'Poppins, sans-serif',
            '&:hover': {
              backgroundColor: '#8BA675',
            },
          }}
          variant="contained"
        >
          Create Role
        </Button>
      </DialogActions>

      {/* Snackbar for Notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
          elevation={6}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

CreateRoleModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  accessToken: PropTypes.string.isRequired,
  selectedGuild: PropTypes.string.isRequired,
  onRoleCreated: PropTypes.func.isRequired,
};

export default CreateRoleModal;

